import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { CoachmarkTour, GLOBAL_COACHMARK_STEPS } from '@models/coachmark.class';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import {
  bufferTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { AppState } from 'src/app';
import { selectCurrentContract } from 'src/app/state/selectors/current-contract.selectors';
import { UserService } from 'src/core';

@Injectable({
  providedIn: 'root',
})
export class CoachmarkService implements OnDestroy {
  /**
   * Indicates when a **valid** tour can start, taking priorization into account
   *
   * It'll receive all the requests to start a tour, and it'll compare against the
   * items in the ordered tours, starting the first one in the list.
   *
   * Valid coachmarks are the ones that:
   *  - Haven't been visited yet (fetched from UserAppConfig)
   *  - Are available for the current contract management (USA/MX)
   */
  public startTour$: Observable<CoachmarkTour>;

  public initializeSubject = new Subject<string>();
  public initTourSubject = new Subject<void>();
  public startTourSubject = new Subject<void>();
  public endTourSubject = new Subject<void>();

  private sortedTours: CoachmarkTour[] = GLOBAL_COACHMARK_STEPS;
  private coachmarkToursVisited: string[];
  private subscriptions: Subscription = new Subscription();
  private tourQueue: string[] = [];

  private contractId: string;
  private activeTour?: string;

  constructor(
    private readonly store: Store<AppState>,
    private readonly userService: UserService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.startTour$ = combineLatest(
      this.initializeSubject.pipe(
        bufferTime(3000),
        filter((e) => !!e.length),
        tap((e) => {
          this.tourQueue = [...this.tourQueue, ...e];
        }),
      ),
      this.getAvailableCoachmarks$(),
    ).pipe(
      map(([_, availableCoachmarks]) =>
        availableCoachmarks.find((availableCoachmark) =>
          // Use the contract management type (USA/MX) to check if the contract
          // is compatible with the coachmark candidate
          // Check if the current availableCoachmark exists in the waiting-to-start tours
          this.tourQueue.includes(availableCoachmark.mainAnchorId),
        ),
      ),
      // Prevent undeifned values from being emitted to the subscriber
      filter((e) => e !== undefined),
      // Remove steps from that tour that are not currently in the dom
      map((tour) => ({
        ...tour,
        steps: tour.steps.filter((step) =>
          this.checkExistanceOfStepAnchorInDom(step.anchorId),
        ),
      })),
      filter((e) => !!e.steps.length),
      tap(({ mainAnchorId }) => (this.activeTour = mainAnchorId)),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public initializeTour(anchorId: string): void {
    this.initializeSubject.next(anchorId);
  }

  public initTour(): void {
    this.initTourSubject.next();
  }

  public startTour(): void {
    this.startTourSubject.next();
  }

  public endTour(): void {
    this.endTourSubject.next();
  }

  private getAvailableCoachmarks$(): Observable<CoachmarkTour[]> {
    return this.store.select(selectCurrentContract).pipe(
      map(({ contractID: contractId, isUsa }) => ({ contractId, isUsa })),
      distinctUntilChanged(
        (a, b) => a.contractId === b.contractId && a.isUsa === b.isUsa,
      ),
      switchMap(({ contractId, isUsa }) => {
        this.contractId = contractId;
        this.tourQueue = [];
        if (this.activeTour) {
          this.endTour();
          this.activeTour = undefined;
        }

        return this.userService.currentUserAppConfiguration$.pipe(
          filter(Boolean),
          map(({ uiConfiguration }) => {
            const coachmarkToursVisited: string[] =
              uiConfiguration &&
              uiConfiguration.coachmarkToursVisited &&
              Array.isArray(uiConfiguration.coachmarkToursVisited)
                ? uiConfiguration.coachmarkToursVisited
                : [];

            this.coachmarkToursVisited = coachmarkToursVisited;

            return this.sortedTours.filter(
              (tour: CoachmarkTour) =>
                (isUsa ? tour.isUsa : tour.isMx) &&
                !coachmarkToursVisited.includes(
                  tour.isOnePerContract
                    ? this.addContractSuffix(tour.mainAnchorId, contractId)
                    : tour.mainAnchorId,
                ),
            );
          }),
        );
      }),
    );
  }

  public removeTourFromList(mainAnchorId: string): void {
    this.tourQueue = this.tourQueue.filter((e) => e !== mainAnchorId);
  }

  public updateCoachmarkTour(tourToUpdate: CoachmarkTour): void {
    const anchorIds = this.getListOfAnchorsFromTour(tourToUpdate);
    anchorIds.forEach((anchorId) => this.removeTourFromList(anchorId));

    this.subscriptions.add(
      this.userService
        .patchUserConfiguration({
          // FIXME: Refactor so we don't need to stringify
          uiConfiguration: JSON.stringify({
            ...JSON.parse(this.userService.lastKnownConfig.uiConfiguration),
            coachmarkToursVisited: [
              ...new Set([
                ...this.coachmarkToursVisited,
                // Also save the tours that should be disabled when
                // completing the given tour
                ...anchorIds,
              ]),
            ],
          }),
        })
        .subscribe(),
    );
  }

  /**
   * Get the list of anchors that are or descend from a parent tour
   *
   * In the most common scenario, this will return just the anchorId of the parent
   * tour in an array.
   *
   * But tours can contain children (`toursToDisable`), in which case we need
   * to also return those anchorIds in the list.
   *
   * And every tour (parent or child), can be a "one per-contract" tour, in which
   * case we append the current contract ID at the end of the anchor. This won't
   * affect the execution flow of the tour.
   *
   * @see{@link CoachmarkStep.isOnePerContract}
   * @see{@link CoachmarkStep.toursToDisable}
   *
   * @param tourToUpdate that may also contain a `toursToDisable`
   * @returns a list of anchors with the contract suffix when necessary
   */
  private getListOfAnchorsFromTour(tourToUpdate: CoachmarkTour): string[] {
    const anchorIds = [
      tourToUpdate.isOnePerContract
        ? this.addContractSuffix(tourToUpdate.mainAnchorId, this.contractId)
        : tourToUpdate.mainAnchorId,
    ];

    if (!tourToUpdate.toursToDisable) {
      return anchorIds;
    }

    return [
      ...anchorIds,
      ...tourToUpdate.toursToDisable.map((tourToDisable) => {
        const fullTourObject = this.sortedTours.find(
          (tour) => tour.mainAnchorId === tourToDisable,
        );

        return fullTourObject && fullTourObject.isOnePerContract
          ? this.addContractSuffix(tourToDisable, this.contractId)
          : tourToDisable;
      }),
    ];
  }

  /**
   * Uses the current contract ID as suffix to a given string, separated by __
   *
   * @param str to append the contract as suffix
   * @returns the original string and the contract separated by "__"
   */
  private addContractSuffix(str: string, contractId: string): string {
    return `${str}___${contractId}`;
  }

  /**
   * Checks if a given step anchor ID exists in the document
   *
   * @param stepAnchorId that may exist in the dom as a data or ng-reflect attribute
   * @returns a flag indicating if it exists or not
   */
  private checkExistanceOfStepAnchorInDom(stepAnchorId: string): boolean {
    if (!this.document || !stepAnchorId) {
      return false;
    }

    /**
     * This contains all the possible selectors that could uniquely refer to a step id.
     *
     * It contains the attributes:
     *
     *   - `[data-tour-anchor]`, which requires to manually add a `data-tour-anchor`
     * attribute to "potential tourAnchors".
     *   - `[ng-reflex-tour-anchor]`, which is **automatically appended**. With our current
     * versions of Angular, ngx-tour, and ngx-tour-ngx-popper, this is the attribute we get,
     * but it may stop being so in future versions. Use this only as a back-up, and prefer
     * adding a `[data-tour-anchor]`
     *
     * ----
     *
     * Adding a `data-tour-anchor` attribute with variable `stepAnchorId`:
     * ```html
     * <div
     *   [tourAnchor]="link.tourAnchor"
     *   [attr.data-tour-anchor]="link.tourAnchor"
     * ></div>
     * ```
     *
     * Adding a `data-tour-anchor` attribute with literal `stepAnchorId`:
     *
     * ```html
     * <div
     *   tourAnchor="my-step-anchor-id"
     *   data-tour-anchor="my-step-anchor-id"
     * ></div>
     * ```
     */
    const possibleSelectors = [
      `[data-tour-anchor="${stepAnchorId}"]`,
      `[ng-reflect-tour-anchor="${stepAnchorId}"]`,
    ];

    return possibleSelectors.some(
      (selector) => !!this.document.querySelector(selector),
    );
  }
}
